import type { Flatfile } from '@flatfile/api-internal'
import { useMemo } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import useResource from '../resources/hooks/useResource'
import getCollection from '../resources/support/getCollection'
import { useIdentity } from './useIdentity'

/**
 * A React hook that provides the current active environment and a setter function to change the active environment ID.
 *
 * The active environment is determined using the following logic:
 * 1. First, it attempts to find an environment matching the persisted `environmentId`.
 * 2. If no matching environment is found, it then looks for the first non-production environment.
 * 3. As a last resort, if no non-production environment is available, it defaults to the first environment in the list.
 *
 * @returns A tuple containing the active `Flatfile.Environment` object and a setter function `setActiveEnvironmentId`.
 *          The setter function takes a string argument which is the ID of the environment to be set as active.
 *
 * @example
 * const {environment, setActiveEnvironmentId} = useActiveEnvironment();
 */
export function useActiveEnvironment(): {
  environment: Flatfile.Environment
  setActiveEnvironment: (environmentId: string) => void
  hasEnvAccess: boolean
} {
  const environmentId = useCurrentEnvironmentStore((e) => e.environmentId)
  const setEnvironmentId = useCurrentEnvironmentStore((e) => e.setEnvironmentId)
  const { hasPermission } = useIdentity()

  const selectedEnvironment = useMemo(() => {
    const environments = getCollection('ENVIRONMENTS').getItems()
    const foundEnv = environments.find((env) => env.id === environmentId)
    const firstNonProdEnv = environments.find((env) => !env.isProd)
    return foundEnv || firstNonProdEnv || environments[0]
  }, [environmentId])

  const active = useResource('ENVIRONMENTS', selectedEnvironment?.id)

  const hasEnvAccess = hasPermission({ resource: active, permissionKey: 'environment:read' })

  return {
    environment: active as Flatfile.Environment,
    setActiveEnvironment: setEnvironmentId,
    hasEnvAccess,
  }
}
// function checks all environments for read access - if user does not have env access this means they are a space level user only
export function useHasEnvironmentsAccess() {
  const envs = getCollection('ENVIRONMENTS')
  const { hasPermission } = useIdentity()

  return !!envs.getItems().filter((env) => hasPermission({ resource: env, permissionKey: 'environment:read' })).length
}

interface ActiveEnvironmentStore {
  environmentId: string | undefined
  setEnvironmentId: (environmentId: string) => void
}

export const useCurrentEnvironmentStore = create<ActiveEnvironmentStore>()(
  persist(
    (set) => ({
      environmentId: undefined,
      setEnvironmentId: (environmentId) => set({ environmentId }),
    }),
    { name: 'selected-env', storage: createJSONStorage(() => localStorage) }
  )
)
